<template>
  <div class="play" tabindex="0" ref="root">
    <!-- The one bar: home, title, clock, menu. Compact on purpose - every
         pixel here comes out of the grid and pushes the keyboard down. -->
    <div class="play__top">
      <button class="icon-button" title="Back to the list" aria-label="Back to the list" @click="$emit('home')">
        <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M8 1.5 1 7.5h2v6h4v-4h2v4h4v-6h2L8 1.5z" />
        </svg>
      </button>
      <div class="play__title">
        <strong>{{ puzzle.title }}</strong>
        <span class="muted">{{ puzzle.giant ? `${puzzle.season} Giant` : puzzle.weekday }} · {{ puzzle.difficulty }}<template v-if="roomMeta?.code"> · <span class="header-code">{{ roomMeta.code }}</span></template></span>
      </div>
      <div class="play__right">
        <span v-if="members.length > 1" class="play__members" :title="members.map((m) => m.name).join(', ')">
          <i v-for="m in members" :key="m.uid" class="dot" :style="{ background: m.color }" />
        </span>
        <span class="play__timer" :class="{ 'is-done': solvedAt, 'is-paused': paused }" :title="paused ? 'Timer paused' : null">{{ time }}</span>
        <button v-if="isGiant" class="icon-button" title="See the whole Giant" aria-label="See the whole Giant" @click="mapOpen = true">
          <svg width="17" height="17" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M1 1h6v6H1zM9 1h6v6H9zM1 9h6v6H1zM9 9h6v6H9z" opacity="0.85" />
          </svg>
        </button>
        <button v-if="roomId" class="icon-button play__chat-button" :title="chatUnread ? `Chat (${chatUnread} new)` : 'Chat'" :aria-label="chatUnread ? `Chat, ${chatUnread} new` : 'Chat'" :aria-pressed="chatShown" @click="toggleChat">
          <svg width="17" height="17" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M2 2.5h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6.5L3 14.5v-3H2a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z" />
          </svg>
          <i v-if="chatUnread && !chatShown" class="play__unread" />
        </button>
        <button class="icon-button" title="More" aria-label="More" @click="menuOpen = !menuOpen">⋯</button>
        <button class="icon-button" title="How to play" aria-label="How to play" @click="$emit('howto')">?</button>
      </div>
    </div>

    <div v-if="menuOpen" class="menu" @click.self="menuOpen = false">
      <div class="menu__panel">
        <button class="menu__item" @click="act('check-square')">Check square</button>
        <button class="menu__item" @click="act('check-word')">Check word</button>
        <button class="menu__item" @click="act('check-puzzle')">Check puzzle</button>
        <hr>
        <button class="menu__item" @click="act('reveal-square')">Reveal square</button>
        <button class="menu__item" @click="act('reveal-word')">Reveal word</button>
        <button class="menu__item menu__item--danger" @click="act('reveal-puzzle')">Reveal puzzle</button>
        <hr>
        <button v-if="!wide" class="menu__item" @click="act('clues')">All clues</button>
        <button class="menu__item" @click="act('share')">{{ roomId ? 'Who’s solving' : 'Solve together' }}</button>
        <button v-if="roomId && roomMeta && !roomMeta.groupId" class="menu__item" @click="act('keep-group')">Keep this group</button>
        <button v-if="!solvedAt" class="menu__item menu__item--danger" @click="act('clear')">Clear puzzle</button>
        <hr>
        <button class="menu__item menu__item--toggle" role="switch" :aria-checked="jumpToNextClue" @click="act('jump-setting')">
          Jump to next clue <span class="menu__state">{{ jumpToNextClue ? 'On' : 'Off' }}</span>
        </button>
        <button class="menu__item menu__item--toggle" role="switch" :aria-checked="wrapInWord" @click="act('wrap-setting')">
          Wrap to start of word <span class="menu__state">{{ wrapInWord ? 'On' : 'Off' }}</span>
        </button>
      </div>
    </div>

    <div class="play__body" :class="{ 'play__body--wide': wide }">
      <div class="play__main" :style="wide ? gridSize : null">
        <div class="play__grid" :style="gridSize">
          <PuzzleGrid :reveal-theme="celebrating || Boolean(solvedAt)" :reveal-step="revealStep" :view="gridView" @tap="focusRoot" @window="windowAt = $event" />
        </div>

        <ClueBar @jump="jump" @toggle="toggle" />

        <PuzzleKeyboard v-if="touch" class="play__keyboard" @key="onSoftKey" />
        <p v-else class="play__hint muted">Type to fill · arrows move · Tab next clue · Space flips direction</p>
      </div>

      <div v-if="wide" class="play__dock">
        <div v-if="roomId" class="play__tabs" role="tablist">
          <button role="tab" :aria-selected="dockTab === 'clues'" :class="{ 'is-on': dockTab === 'clues' }" @click="dockTab = 'clues'; focusRoot()">Clues</button>
          <button role="tab" :aria-selected="dockTab === 'chat'" :class="{ 'is-on': dockTab === 'chat' }" @click="dockTab = 'chat'">
            Chat<i v-if="chatUnread && dockTab !== 'chat'" class="play__unread play__unread--tab" />
          </button>
        </div>
        <ChatPanel v-if="roomId && dockTab === 'chat'" docked />
        <ClueList v-else docked />
      </div>

      <!-- Idle for two minutes: the clock has stopped, and nothing of the
           puzzle shows or takes a letter until a tap or key (Matt 2026-10-27). -->
      <button v-if="paused" class="play__paused" @click="resume">
        <strong>Timer paused</strong>
        <span>{{ roomId ? 'Nobody has touched the puzzle for two minutes.' : 'You haven’t touched the puzzle for two minutes.' }}</span>
        <span class="muted">Tap or press any key to keep going</span>
      </button>
    </div>

    <ClueList v-if="cluesOpen && !wide" @close="cluesOpen = false" />
    <ChatPanel v-if="chatOpen && roomId && !wide" @close="chatOpen = false; focusRoot()" />
    <ShareSheet v-if="shareOpen" :keep="keepOpen" @close="shareOpen = false; keepOpen = false" />
    <GiantMap v-if="isGiant && mapOpen" :window-at="windowAt" :revealing="celebrating && !sheetOpen" :reveal-step="revealStep" @close="mapOpen = false; focusRoot()" />
    <SolvedSheet v-if="solvedAt && sheetOpen" @close="sheetOpen = false" />

    <!-- Full but not right: without this a finished-but-wrong grid just sat
         there (Seth, 2026-10-09). Not modal - typing carries on under it. -->
    <div v-if="fullNotice && !solvedAt" class="play__full" role="status" aria-live="polite">
      <p>Every square is filled, but something isn’t right yet.</p>
      <div class="play__full-actions">
        <button class="secondary" @click="dismissFull">Keep looking</button>
        <button class="primary" @click="checkFromFull">Check puzzle</button>
      </div>
    </div>

    <div v-if="toast" class="play__toast">{{ toast }}</div>

    <!-- Someone else said something while the chat was out of sight. -->
    <button v-if="ping && !chatShown" class="play__ping" @click="ping = null; toggleChat()">
      <strong :style="{ color: ping.color }">{{ ping.name }}</strong> {{ ping.text }}
    </button>
  </div>
</template>

<script>
// The solving screen: title and clock, grid, the clue bar, the keyboard.
// One column, sized so the whole thing fits a phone with the keyboard up;
// the grid takes what is left of the height and never more than the width.
import { mapState, mapGetters } from 'vuex';
import PuzzleGrid from './PuzzleGrid.vue';
import PuzzleKeyboard from './Keyboard.vue';
import ClueBar from './ClueBar.vue';
import ClueList from './ClueList.vue';
import ShareSheet from './ShareSheet.vue';
import SolvedSheet from './SolvedSheet.vue';
import GiantMap from './GiantMap.vue';
import ChatPanel from './ChatPanel.vue';
import { formatTime, whiteCells } from '@/engine/solving';
import { revealPlan, reduceMotion, GIANT_STEP_MS } from '@/utils/revealTheatre';

// Room for the grid AND every clue: a desktop, or an iPad on its side.
const wideScreen = () => typeof window !== 'undefined'
  && window.matchMedia('(min-width: 1000px) and (orientation: landscape)').matches;

export default {
  name: 'PlayScreen',
  emits: ['home', 'howto'],
  components: {
    PuzzleGrid, PuzzleKeyboard, ClueBar, ClueList, ShareSheet, SolvedSheet, GiantMap, ChatPanel,
  },
  data() {
    return {
      menuOpen: false,
      cluesOpen: false,
      shareOpen: false,
      keepOpen: false,
      sheetOpen: false,
      mapOpen: false,
      chatOpen: false, // the chat sheet (phone)
      dockTab: 'clues', // beside the grid (wide): 'clues' or 'chat'
      ping: null, // the pop-up preview of someone's message
      windowAt: null,
      celebrating: false,
      revealStep: 0,
      revealTimers: [],
      toast: '',
      // ?touch=1 forces the phone layout in a desktop browser - for checking
      // it in a 390px iframe without a phone in hand.
      touch: typeof window !== 'undefined' && (window.matchMedia('(pointer: coarse)').matches || new URLSearchParams(window.location.search).has('touch')),
      viewportH: typeof window !== 'undefined' ? window.innerHeight : 800,
      wide: wideScreen(),
    };
  },
  computed: {
    ...mapState(['puzzle', 'cursor', 'seconds', 'solvedAt', 'roomId', 'roomMeta', 'fullNotice', 'chatPing', 'clockPaused']),
    ...mapGetters(['currentCells', 'members', 'inRoom', 'jumpToNextClue', 'wrapInWord', 'isGiant', 'chatUnread']),
    chatShown() { return Boolean(this.roomId) && (this.wide ? this.dockTab === 'chat' : this.chatOpen); },
    // A Giant is seen through a window this many squares across: a daily's
    // size on a phone, more where the screen has room.
    gridView() {
      if (!this.isGiant) return 0;
      if (this.wide) return 25;
      return this.touch ? 15 : 21;
    },
    time() { return formatTime(this.seconds); },
    paused() { return this.clockPaused && !this.solvedAt; },
    // Grid edge = the smaller of the width and the height left over after
    // the chrome: the bar (~46 + safe top), clue bar 60 (crossing clue included) + its 8 gap, the keyboard with
    // its thumb-lift on touch (~210), the build stamp (~30).
    gridSize() {
      const chrome = 46 + 30 + 60 + 8 + (this.touch ? 210 : 28) + 30;
      // Beside the docked clues there is room to grow past a phone's cap.
      const edge = Math.max(220, Math.min(this.viewportH - chrome, this.wide ? 820 : 640));
      return { maxWidth: `${edge}px` };
    },
  },
  watch: {
    cursor: { handler() { this.$store.dispatch('publishPresence'); }, deep: true },
    solvedAt(at, was) {
      if (at && !was) this.celebrate();
    },
    chatPing(message) {
      if (!message || this.chatShown) return;
      this.ping = { ...message, text: message.text.length > 90 ? `${message.text.slice(0, 88)}…` : message.text };
      clearTimeout(this.pingTimer);
      this.pingTimer = setTimeout(() => { this.ping = null; }, 5000);
    },
    roomId() { this.chatOpen = false; this.dockTab = 'clues'; this.ping = null; },
    // The cover hides the clues and the map as well as the grid.
    paused(on) {
      if (on) { this.cluesOpen = false; this.mapOpen = false; this.menuOpen = false; }
    },
  },
  mounted() {
    this.onResize = () => { this.viewportH = window.innerHeight; this.wide = wideScreen(); };
    window.addEventListener('resize', this.onResize);
    // Keys are taken at the window, not the grid: whatever has focus after a
    // sheet closes, typing fills the puzzle - unless a real text field has it.
    this.onWindowKey = (e) => {
      const tag = document.activeElement?.tagName || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || document.activeElement?.isContentEditable) return;
      this.onKey(e);
    };
    window.addEventListener('keydown', this.onWindowKey);
    // Any tap keeps the clock going - but a tap on the cover only resumes
    // (its click does), so it can't land on a square underneath.
    this.onWindowPointer = (e) => {
      if (e.target?.closest?.('.play__paused')) return;
      if (e.type === 'pointermove' && (e.pointerType !== 'mouse' || Date.now() - this.movedAt < 1000)) return;
      this.movedAt = Date.now();
      this.$store.dispatch('noteActivity');
    };
    this.movedAt = 0;
    window.addEventListener('pointerdown', this.onWindowPointer, true);
    window.addEventListener('pointermove', this.onWindowPointer, true);
    this.focusRoot();
    if (this.solvedAt) { this.revealStep = 99; }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onWindowKey);
    window.removeEventListener('pointerdown', this.onWindowPointer, true);
    window.removeEventListener('pointermove', this.onWindowPointer, true);
    this.revealTimers.forEach((t) => clearTimeout(t));
    clearTimeout(this.pingTimer);
  },
  methods: {
    focusRoot() { if (!this.touch) this.$refs.root?.focus(); },
    jump(delta) { this.$store.dispatch('jumpClue', delta); },
    toggle() { this.$store.dispatch('toggleDir'); },
    toggleChat() {
      this.ping = null;
      if (this.wide) {
        this.dockTab = this.dockTab === 'chat' ? 'clues' : 'chat';
        if (this.dockTab === 'clues') this.focusRoot();
      } else {
        this.chatOpen = !this.chatOpen;
      }
    },
    onSoftKey(k) {
      if (k === '⌫') this.$store.dispatch('backspace');
      else if (k === '⇄') this.toggle();
      else this.$store.dispatch('typeLetter', k);
    },
    resume() {
      this.$store.dispatch('noteActivity');
      this.focusRoot();
    },
    onKey(e) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Paused: the key only wakes the clock; it types nothing.
      if (this.paused) { e.preventDefault(); this.resume(); return; }
      this.$store.dispatch('noteActivity');
      const { key } = e;
      if (/^[a-zA-Z]$/.test(key)) { e.preventDefault(); this.$store.dispatch('typeLetter', key); return; }
      const map = {
        Backspace: () => this.$store.dispatch('backspace'),
        Delete: () => this.$store.dispatch('backspace'),
        ArrowLeft: () => this.$store.dispatch('moveCursor', { dCol: -1 }),
        ArrowRight: () => this.$store.dispatch('moveCursor', { dCol: 1 }),
        ArrowUp: () => this.$store.dispatch('moveCursor', { dRow: -1 }),
        ArrowDown: () => this.$store.dispatch('moveCursor', { dRow: 1 }),
        Tab: () => this.jump(e.shiftKey ? -1 : 1),
        Enter: () => this.jump(1),
        ' ': () => this.toggle(),
        Escape: () => { this.menuOpen = false; this.cluesOpen = false; this.chatOpen = false; },
      };
      if (map[key]) { e.preventDefault(); map[key](); }
    },
    async act(what) {
      this.menuOpen = false;
      const { row, col } = this.cursor;
      const all = whiteCells(this.puzzle);
      const say = (n, scope) => this.showToast(n === 0 ? `${scope}: all correct so far` : `${scope}: ${n} wrong`);
      switch (what) {
        case 'check-square': say(await this.$store.dispatch('checkCells', [[row, col]]), 'Square'); break;
        case 'check-word': say(await this.$store.dispatch('checkCells', this.currentCells), 'Word'); break;
        case 'check-puzzle': say(await this.$store.dispatch('checkCells', all), 'Puzzle'); break;
        case 'reveal-square': this.$store.dispatch('revealCells', [[row, col]]); break;
        case 'reveal-word': this.$store.dispatch('revealCells', this.currentCells); break;
        case 'reveal-puzzle':
          // eslint-disable-next-line no-alert -- a deliberate, single destructive confirm
          if (window.confirm('Reveal the whole puzzle? It will not count as a clean solve.')) this.$store.dispatch('revealCells', all);
          break;
        case 'clear':
          // eslint-disable-next-line no-alert -- a deliberate, single destructive confirm
          if (window.confirm('Clear every letter you have entered?')) this.$store.dispatch('clearPuzzle');
          break;
        case 'clues': if (!this.wide) this.cluesOpen = true; break;
        case 'share': this.shareOpen = true; break;
        case 'keep-group': this.keepOpen = true; this.shareOpen = true; break;
        case 'jump-setting':
          this.$store.commit('setSettings', { jumpToNextClue: !this.jumpToNextClue });
          this.showToast(this.jumpToNextClue ? 'Jumps to the next clue after a word' : 'Stays put at the end of a word');
          break;
        case 'wrap-setting':
          this.$store.commit('setSettings', { wrapInWord: !this.wrapInWord });
          this.showToast(this.wrapInWord ? 'Wraps back to empty squares in the word' : 'Leaves the word at its last square');
          break;
        default: break;
      }
      this.focusRoot();
    },
    dismissFull() {
      this.$store.commit('setFullNotice', false);
      this.focusRoot();
    },
    checkFromFull() {
      this.$store.commit('setFullNotice', false);
      this.act('check-puzzle');
    },
    showToast(text) {
      this.toast = text;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toast = ''; }, 2200);
    },
    // The reveal: theme entries light up one at a time, then the sheet
    // slides in. A tap anywhere skips to the sheet. Reduced motion: at once.
    // A Giant's theme is spread over the whole grid, so its reveal plays on
    // the map, a little quicker per entry (there are thirty-odd).
    celebrate() {
      const entries = [...this.puzzle.across, ...this.puzzle.down].filter((e) => e.theme).length;
      const plan = revealPlan(entries, reduceMotion(), this.isGiant ? GIANT_STEP_MS : undefined);
      if (this.isGiant) this.mapOpen = true;
      this.celebrating = true;
      this.revealStep = 0;
      this.revealTimers = plan.steps.map((at, i) => setTimeout(() => { this.revealStep = i + 1; }, at));
      this.revealTimers.push(setTimeout(() => { this.sheetOpen = true; }, plan.sheetAt));
    },
  },
};
</script>

<style lang="scss" scoped>
.play {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  outline: none;
}

.play__top {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  // Under the status bar / Dynamic Island, like the app header it replaces.
  padding: var(--band-top) 0.35rem 0.15rem 0.3rem;
  border-bottom: 1px solid var(--edge);
  background: rgba(0, 0, 0, 0.15);
}
.play__title { flex: 1; display: flex; flex-direction: column; min-width: 0; line-height: 1.15;
  strong { font-size: 0.92rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .muted { font-size: 0.68rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; } }
.play__right { display: flex; align-items: center; gap: 0.15rem; flex-shrink: 0; }
.play__timer { font-variant-numeric: tabular-nums; font-weight: 700; font-size: 0.95rem; color: var(--ink-dim);
  &.is-done { color: var(--accent); }
  &.is-paused { opacity: 0.55; } }
.play__members { display: inline-flex; gap: 3px; }
.play__chat-button { position: relative; }
.play__unread {
  position: absolute; top: 6px; right: 6px; width: 8px; height: 8px; border-radius: 50%;
  background: var(--accent); box-shadow: 0 0 0 2px var(--felt-deep);
  &--tab { position: static; display: inline-block; margin-left: 0.35rem; vertical-align: 0.15em; box-shadow: none; }
}
.dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; }

.play__body { flex: 1; min-height: 0; display: flex; flex-direction: column; position: relative; }

.play__paused {
  position: absolute; inset: 0; z-index: 25;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem;
  padding: 1.5rem; text-align: center; color: var(--ink);
  background: var(--felt-deep, #141821);
  strong { font-size: 1.4rem; }
  span { font-size: 0.95rem; max-width: 22rem; }
}
.play__main { flex: 1; min-height: 0; display: flex; flex-direction: column; }
// Wide: the grid and its clue bar on the left, every clue on the right.
.play__body--wide {
  flex-direction: row; justify-content: center; align-items: stretch; gap: 1.25rem;
  padding: 0.9rem 1.25rem 0;
  .play__main { flex: 0 1 auto; width: 100%; }
  .play__grid { padding: 0; }
  // Its own panel under the grid, not a strip stuck to it.
  .cluebar { border-radius: var(--radius); }
  .play__dock { flex: 0 1 620px; min-width: 340px; margin-bottom: 1rem; min-height: 0; display: flex; flex-direction: column; gap: 0.5rem; }
}
.play__dock :deep(.clues-dock) { flex: 1; }
.play__tabs {
  display: flex; gap: 0.3rem;
  button {
    padding: 0.35rem 0.9rem; border-radius: 999px; font-size: 0.85rem; color: var(--ink-dim);
    border: 1px solid var(--edge);
    &.is-on { background: var(--accent); border-color: var(--accent); color: #211a05; font-weight: 700; }
  }
}

// The grid hugs the bar; whatever height is spare sits between the grid
// and the clue bar, not above the grid.
.play__grid {
  flex: 1;
  min-height: 0;
  width: 100%;
  margin: 0 auto;
  padding: 0.4rem 0.5rem 0;
  display: flex;
  align-items: flex-start;
}

// Lifted off the bottom edge: pinned flush, the bottom row sat just below
// where a thumb rests (Matt, 2026-10-09: "probably by about 50 pixels").
.play__keyboard { margin-bottom: 48px; }

.play__hint { text-align: center; font-size: 0.72rem; margin: 0.3rem 0 calc(0.3rem + var(--safe-bottom)); }

.menu {
  position: fixed; inset: 0; z-index: 30;
}
.menu__panel {
  position: absolute; right: 0.6rem; top: calc(var(--band-top) + 44px);
  background: var(--surface-raised); border: 1px solid var(--edge); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 0.3rem; min-width: 190px;
  hr { border: 0; border-top: 1px solid var(--edge); margin: 0.3rem 0; }
}
.menu__item {
  display: block; width: 100%; text-align: left; padding: 0.45rem 0.7rem; border-radius: 8px; font-size: 0.9rem;
  &:active { background: rgba(255,255,255,0.08); }
  &--danger { color: var(--danger); }
  &--toggle { display: flex; justify-content: space-between; gap: 1rem; }
}
.menu__state { opacity: 0.6; }

.play__full {
  position: fixed; left: 50%; bottom: calc(220px + var(--safe-bottom)); transform: translateX(-50%);
  width: min(340px, calc(100vw - 2rem)); z-index: 40;
  background: var(--surface-raised); border: 1px solid var(--edge); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 0.85rem 0.9rem 0.8rem;
  p { margin: 0 0 0.7rem; font-size: 0.92rem; text-align: center; }
}
.play__full-actions {
  display: flex; gap: 0.5rem;
  button { flex: 1; padding: 0.55rem; font-size: 0.9rem; }
}

.play__ping {
  position: fixed; left: 50%; top: calc(var(--band-top) + 52px); transform: translateX(-50%);
  width: max-content; max-width: min(420px, calc(100vw - 1.5rem)); z-index: 45; text-align: left;
  background: var(--surface-raised); border: 1px solid var(--edge); color: var(--ink);
  padding: 0.5rem 0.85rem; border-radius: 14px; font-size: 0.88rem; line-height: 1.3; box-shadow: var(--shadow);
  overflow-wrap: anywhere;
}

.play__toast {
  position: fixed; left: 50%; bottom: calc(220px + var(--safe-bottom)); transform: translateX(-50%);
  background: var(--surface-raised); border: 1px solid var(--edge); color: var(--ink);
  padding: 0.5rem 0.9rem; border-radius: 999px; font-size: 0.85rem; box-shadow: var(--shadow); z-index: 40;
}
</style>
